import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, KeyRound, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, BACKEND_URL, errorText, fmtTime } from "@/lib/api";
import { ApiGuide } from "@/components/onboarding/ApiGuide";

const copy = (text) => navigator.clipboard.writeText(text).then(() => toast.success("Copied"), () => toast.error("Couldn't copy, select it instead"));

const NewKey = ({ k, onDone }) => (
  <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-4 space-y-2" data-testid="integration-new-key">
    <p className="text-sm text-emerald-300">Copy this key now. It won't be shown again.</p>
    <div className="flex flex-col sm:flex-row gap-2">
      <code className="flex-1 min-w-0 break-all rounded border border-[#222D3D] bg-[#07090E] px-3 py-2 text-xs text-slate-100" data-testid="integration-new-key-value">{k}</code>
      <Button size="sm" variant="outline" onClick={() => copy(k)} className="border-[#222D3D] bg-transparent" data-testid="integration-copy-key-btn"><Copy className="h-4 w-4 mr-2" />Copy</Button>
    </div>
    <button type="button" onClick={onDone} className="text-xs text-slate-400 underline" data-testid="integration-new-key-done">I've saved it</button>
  </div>
);

export const IntegrationsPanel = ({ msp = false }) => {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/integrations").then((r) => setKeys(r.data)).catch((e) => toast.error(errorText(e))), []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await api.post("/integrations", { name }); setFresh(r.data.key); setName(""); load(); } catch (err) { toast.error(errorText(err)); }
    setBusy(false);
  };
  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Software using it will lose access immediately.`)) return;
    try { await api.delete(`/integrations/${k.id}`); toast.success("Key revoked"); load(); } catch (err) { toast.error(errorText(err)); }
  };

  return (
    <section className="space-y-6" data-testid="integrations-panel">
      <div className="max-w-2xl">
        <h2 className="text-lg font-semibold text-slate-100">Software validation access</h2>
        <p className="mt-1 text-sm text-slate-400">Give outside software, such as monitoring, service-desk or security tools, a read-only key. It can search, filter and export the agent inventory{msp ? " of all your properties" : ""}, confirm what it saw against the network edge, and verify every record's chain and Bitcoin proof.</p>
      </div>
      <form onSubmit={create} className="flex flex-col sm:flex-row gap-2 max-w-xl" data-testid="integration-create-form">
        <Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} placeholder="Name, e.g. Monitoring platform" className="bg-[#0F141C]" data-testid="integration-name-input" />
        <Button type="submit" disabled={busy} className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold shrink-0" data-testid="integration-create-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4 mr-2" />Create key</>}
        </Button>
      </form>
      {fresh && <NewKey k={fresh} onDone={() => setFresh(null)} />}
      {keys.length ? (
        <ul className="rounded-md border border-[#222D3D] bg-[#0F141C] divide-y divide-[#222D3D]" data-testid="integration-keys-list">
          {keys.map((k) => (
            <li key={k.id} className="flex items-center justify-between gap-3 p-4" data-testid={`integration-key-${k.id}`}>
              <div className="min-w-0">
                <p className="text-slate-100 truncate">{k.name}</p>
                <p className="font-mono text-xs text-slate-500">{k.prefix}… · created {fmtTime(k.created_at)} · {k.last_used_at ? `last used ${fmtTime(k.last_used_at)}` : "never used"}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => revoke(k)} className="text-rose-300 hover:text-rose-200 shrink-0" data-testid={`integration-revoke-btn-${k.id}`}><Trash2 className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Revoke</span></Button>
            </li>
          ))}
        </ul>
      ) : <p className="text-sm text-slate-500" data-testid="integration-keys-empty">No keys yet.</p>}
      <ApiGuide base={BACKEND_URL} msp={msp} />
    </section>
  );
};
